export type BibleVersions = {
  id: string;
  name: string;
  apiID: string;
};

export type BibleBook = {
  key: string;
  name: string;
  testament: 'old' | 'new';
  chapters: number;
  verses: number;
};

const SINGLE_CHAPTER_BOOKS = [
  'obadiah',
  'philemon',
  '2 john',
  '3 john',
  'jude',
];

export const BOOK_ABBREV: Record<string, string> = {
  genesis: 'Gen',
  exodus: 'Exod',
  leviticus: 'Lev',
  numbers: 'Num',
  deuteronomy: 'Deut',
  joshua: 'Josh',
  judges: 'Judg',
  ruth: 'Ruth',
  '1-samuel': '1 Sam',
  '2-samuel': '2 Sam',
  '1-kings': '1 Kgs',
  '2-kings': '2 Kgs',
  '1-chronicles': '1 Chr',
  '2-chronicles': '2 Chr',
  ezra: 'Ezra',
  nehemiah: 'Neh',
  esther: 'Esth',
  job: 'Job',
  psalms: 'Ps',
  proverbs: 'Prov',
  ecclesiastes: 'Eccl',
  'song-of-solomon': 'Song',
  isaiah: 'Isa',
  jeremiah: 'Jer',
  lamentations: 'Lam',
  ezekiel: 'Ezek',
  daniel: 'Dan',
  hosea: 'Hos',
  joel: 'Joel',
  amos: 'Amos',
  obadiah: 'Obad',
  jonah: 'Jonah',
  micah: 'Mic',
  nahum: 'Nah',
  habakkuk: 'Hab',
  zephaniah: 'Zeph',
  haggai: 'Hag',
  zechariah: 'Zech',
  malachi: 'Mal',
  matthew: 'Matt',
  mark: 'Mk',
  luke: 'Lk',
  john: 'Jn',
  acts: 'Acts',
  romans: 'Rom',
  '1-corinthians': '1 Cor',
  '2-corinthians': '2 Cor',
  galatians: 'Gal',
  ephesians: 'Eph',
  philippians: 'Phil',
  colossians: 'Col',
  '1-thessalonians': '1 Thess',
  '2-thessalonians': '2 Thess',
  '1-timothy': '1 Tim',
  '2-timothy': '2 Tim',
  titus: 'Tit',
  philemon: 'Phlm',
  hebrews: 'Heb',
  james: 'Jas',
  '1-peter': '1 Pet',
  '2-peter': '2 Pet',
  '1-john': '1 Jn',
  '2-john': '2 Jn',
  '3-john': '3 Jn',
  jude: 'Jude',
  revelation: 'Rev',
};

// Chapter verse counts per book
export const CHAPTER_VERSE_COUNTS: Record<string, number[]> = {
  genesis: [
    31, 25, 24, 26, 32, 22, 24, 22, 29, 32, 32, 20, 18, 24, 21, 16, 27, 33, 38,
    18, 34, 24, 20, 67, 34, 35, 46, 22, 35, 43, 55, 32, 20, 31, 29, 43, 36, 30,
    23, 23, 57, 38, 34, 34, 28, 34, 31, 22, 33, 26,
  ],
  exodus: [
    22, 25, 22, 31, 23, 30, 25, 32, 35, 29, 10, 51, 22, 31, 27, 36, 16, 27, 25,
    26, 36, 31, 33, 18, 40, 37, 21, 43, 46, 38, 18, 35, 23, 35, 35, 38, 29, 31,
    43, 38,
  ],
  leviticus: [
    17, 16, 17, 35, 19, 30, 38, 36, 24, 20, 47, 8, 59, 57, 33, 34, 16, 30, 37,
    27, 24, 33, 44, 23, 55, 46, 34,
  ],
  numbers: [
    54, 34, 51, 49, 31, 27, 89, 26, 23, 36, 35, 16, 33, 45, 41, 50, 13, 32, 22,
    29, 35, 41, 30, 25, 18, 65, 23, 31, 40, 16, 54, 42, 56, 29, 34, 13,
  ],
  deuteronomy: [
    46, 37, 29, 49, 33, 25, 26, 20, 29, 22, 32, 32, 18, 29, 23, 22, 20, 22, 21,
    20, 23, 30, 25, 22, 19, 19, 26, 68, 29, 20, 30, 52, 29, 12,
  ],
  joshua: [
    18, 24, 17, 24, 15, 27, 26, 35, 27, 43, 23, 24, 33, 15, 63, 10, 18, 28, 51,
    9, 45, 34, 16, 33,
  ],
  judges: [
    36, 23, 31, 24, 31, 40, 25, 35, 57, 18, 40, 15, 25, 20, 20, 31, 13, 31, 30,
    48, 25,
  ],
  ruth: [22, 23, 18, 22],
  '1-samuel': [
    28, 36, 21, 22, 12, 21, 17, 22, 27, 27, 15, 25, 23, 52, 35, 23, 58, 30, 24,
    42, 15, 23, 29, 22, 44, 25, 12, 25, 11, 31, 13,
  ],
  '2-samuel': [
    27, 32, 39, 12, 25, 23, 29, 18, 13, 19, 27, 31, 39, 33, 37, 23, 29, 33, 43,
    26, 22, 51, 39, 25,
  ],
  '1-kings': [
    53, 46, 28, 20, 32, 38, 51, 66, 28, 29, 43, 33, 34, 31, 34, 34, 24, 46, 21,
    43, 29, 53,
  ],
  '2-kings': [
    18, 25, 27, 44, 27, 33, 20, 29, 37, 36, 21, 21, 25, 29, 38, 20, 41, 37, 37,
    21, 26, 20, 37, 20, 30,
  ],
  '1-chronicles': [
    54, 55, 24, 43, 26, 81, 40, 40, 44, 14, 47, 40, 14, 17, 29, 43, 27, 17, 19,
    8, 30, 19, 32, 31, 31, 32, 34, 21, 30,
  ],
  '2-chronicles': [
    17, 18, 17, 22, 14, 42, 22, 18, 31, 19, 23, 16, 22, 15, 19, 14, 19, 34, 11,
    37, 20, 12, 21, 27, 28, 23, 9, 27, 36, 27, 21, 33, 25, 33, 27, 23,
  ],
  ezra: [11, 70, 13, 24, 17, 22, 28, 36, 15, 44],
  nehemiah: [11, 20, 32, 23, 19, 19, 73, 18, 38, 39, 36, 47, 31],
  esther: [22, 23, 15, 17, 14, 14, 10, 17, 32, 3],
  job: [
    22, 13, 26, 21, 27, 30, 21, 22, 35, 22, 20, 25, 28, 22, 35, 22, 16, 21, 29,
    29, 34, 30, 17, 25, 6, 14, 23, 28, 25, 31, 40, 22, 33, 37, 16, 33, 24, 41,
    30, 32, 26, 17,
  ],
  psalms: [
    6, 12, 8, 8, 12, 10, 17, 9, 20, 18, 7, 8, 6, 7, 5, 11, 15, 50, 14, 9, 13,
    31, 6, 10, 22, 12, 14, 9, 11, 12, 24, 11, 22, 22, 28, 12, 40, 22, 13, 17,
    13, 11, 5, 26, 17, 11, 9, 14, 20, 23, 19, 9, 6, 7, 23, 13, 11, 11, 17, 12,
    8, 12, 11, 10, 13, 20, 7, 35, 36, 5, 24, 20, 28, 23, 10, 12, 20, 72, 13, 19,
    16, 8, 18, 12, 13, 17, 7, 18, 52, 17, 16, 15, 5, 23, 11, 13, 12, 9, 9, 5, 8,
    28, 22, 35, 45, 48, 43, 13, 31, 7, 10, 10, 9, 8, 18, 19, 2, 29, 176, 7, 8,
    9, 4, 8, 5, 6, 5, 6, 8, 8, 3, 18, 3, 3, 21, 26, 9, 8, 24, 13, 10, 7, 12, 15,
    21, 10, 20, 14, 9, 6,
  ],
  proverbs: [
    33, 22, 35, 27, 23, 35, 27, 36, 18, 32, 31, 28, 25, 35, 33, 33, 28, 24, 29,
    30, 31, 29, 35, 34, 28, 28, 27, 28, 27, 33, 31,
  ],
  ecclesiastes: [18, 26, 22, 16, 20, 12, 29, 17, 18, 20, 10, 14],
  'song-of-solomon': [17, 17, 11, 16, 16, 13, 13, 14],
  isaiah: [
    31, 22, 26, 6, 30, 13, 25, 23, 20, 34, 16, 6, 22, 32, 9, 14, 14, 7, 25, 6,
    17, 25, 18, 23, 12, 21, 13, 29, 24, 33, 9, 20, 24, 17, 10, 22, 38, 22, 8,
    31, 29, 25, 28, 28, 25, 13, 15, 22, 26, 11, 23, 15, 12, 17, 13, 12, 21, 14,
    21, 22, 11, 12, 19, 12, 25, 24,
  ],
  jeremiah: [
    19, 37, 25, 31, 31, 30, 34, 22, 26, 25, 23, 17, 27, 22, 21, 21, 27, 23, 15,
    18, 14, 30, 40, 10, 38, 24, 22, 17, 32, 24, 40, 44, 26, 22, 19, 32, 21, 28,
    18, 16, 18, 22, 13, 30, 5, 28, 7, 47, 39, 46, 64, 34,
  ],
  lamentations: [22, 22, 66, 22, 22],
  ezekiel: [
    28, 10, 27, 17, 17, 14, 27, 18, 11, 22, 25, 28, 23, 23, 8, 63, 24, 32, 14,
    49, 32, 31, 49, 27, 17, 21, 36, 26, 21, 26, 18, 32, 33, 31, 15, 38, 28, 23,
    29, 49, 26, 20, 27, 31, 25, 24, 23, 35,
  ],
  daniel: [21, 49, 30, 37, 31, 28, 28, 27, 27, 21, 45, 13],
  hosea: [11, 23, 5, 19, 15, 11, 16, 14, 17, 15, 11, 15, 16, 9],
  joel: [20, 32, 21],
  amos: [15, 16, 15, 13, 27, 14, 17, 14, 15],
  obadiah: [21],
  jonah: [17, 10, 10, 11],
  micah: [16, 13, 12, 13, 15, 16, 20],
  nahum: [15, 13, 19],
  habakkuk: [17, 20, 19],
  zephaniah: [18, 15, 20],
  haggai: [15, 23],
  zechariah: [21, 13, 10, 14, 11, 15, 14, 23, 17, 12, 17, 14, 9, 21],
  malachi: [14, 17, 18, 6],
  matthew: [
    25, 23, 17, 25, 48, 34, 29, 34, 38, 42, 30, 50, 58, 36, 39, 28, 27, 35, 30,
    34, 46, 46, 39, 51, 46, 75, 66, 20,
  ],
  mark: [45, 28, 35, 41, 43, 56, 37, 38, 50, 52, 33, 44, 37, 72, 47, 20],
  luke: [
    80, 52, 38, 44, 39, 49, 50, 56, 62, 42, 54, 59, 35, 35, 32, 31, 37, 43, 48,
    47, 38, 71, 56, 53,
  ],
  john: [
    51, 25, 36, 54, 47, 71, 53, 59, 41, 42, 57, 50, 38, 31, 27, 33, 26, 40, 42,
    31, 25,
  ],
  acts: [
    26, 47, 26, 37, 42, 15, 60, 40, 43, 48, 30, 25, 52, 28, 41, 40, 34, 28, 41,
    38, 40, 30, 35, 27, 27, 32, 44, 31,
  ],
  romans: [32, 29, 31, 25, 21, 23, 25, 39, 33, 21, 36, 21, 14, 23, 33, 27],
  '1-corinthians': [
    31, 16, 23, 21, 13, 20, 40, 13, 27, 33, 34, 31, 13, 40, 58, 24,
  ],
  '2-corinthians': [24, 17, 18, 18, 21, 18, 16, 24, 15, 18, 33, 21, 14],
  galatians: [24, 21, 29, 31, 26, 18],
  ephesians: [23, 22, 21, 32, 33, 24],
  philippians: [30, 30, 21, 23],
  colossians: [29, 23, 25, 18],
  '1-thessalonians': [10, 20, 13, 18, 28],
  '2-thessalonians': [12, 17, 18],
  '1-timothy': [20, 15, 16, 16, 25, 21],
  '2-timothy': [18, 26, 17, 22],
  titus: [16, 15, 15],
  philemon: [25],
  hebrews: [14, 18, 19, 16, 14, 20, 28, 13, 28, 39, 40, 29, 25],
  james: [27, 26, 18, 17, 20],
  '1-peter': [25, 25, 22, 19, 14],
  '2-peter': [21, 22, 18],
  '1-john': [10, 29, 24, 21, 21],
  '2-john': [13],
  '3-john': [14],
  jude: [25],
  revelation: [
    20, 29, 22, 11, 14, 17, 17, 13, 21, 11, 19, 17, 18, 20, 8, 21, 18, 24, 21,
    15, 27, 21,
  ],
};

export const BIBLE_BOOKS: BibleBook[] = [
  {
    key: 'genesis',
    name: 'Genesis',
    testament: 'old',
    chapters: 50,
    verses: 1533,
  },
  {
    key: 'exodus',
    name: 'Exodus',
    testament: 'old',
    chapters: 40,
    verses: 1213,
  },
  {
    key: 'leviticus',
    name: 'Leviticus',
    testament: 'old',
    chapters: 27,
    verses: 859,
  },
  {
    key: 'numbers',
    name: 'Numbers',
    testament: 'old',
    chapters: 36,
    verses: 1288,
  },
  {
    key: 'deuteronomy',
    name: 'Deuteronomy',
    testament: 'old',
    chapters: 34,
    verses: 959,
  },
  {
    key: 'joshua',
    name: 'Joshua',
    testament: 'old',
    chapters: 24,
    verses: 658,
  },
  {
    key: 'judges',
    name: 'Judges',
    testament: 'old',
    chapters: 21,
    verses: 618,
  },
  { key: 'ruth', name: 'Ruth', testament: 'old', chapters: 4, verses: 85 },
  {
    key: '1-samuel',
    name: '1 Samuel',
    testament: 'old',
    chapters: 31,
    verses: 810,
  },
  {
    key: '2-samuel',
    name: '2 Samuel',
    testament: 'old',
    chapters: 24,
    verses: 695,
  },
  {
    key: '1-kings',
    name: '1 Kings',
    testament: 'old',
    chapters: 22,
    verses: 816,
  },
  {
    key: '2-kings',
    name: '2 Kings',
    testament: 'old',
    chapters: 25,
    verses: 719,
  },
  {
    key: '1-chronicles',
    name: '1 Chronicles',
    testament: 'old',
    chapters: 29,
    verses: 942,
  },
  {
    key: '2-chronicles',
    name: '2 Chronicles',
    testament: 'old',
    chapters: 36,
    verses: 822,
  },
  { key: 'ezra', name: 'Ezra', testament: 'old', chapters: 10, verses: 280 },
  {
    key: 'nehemiah',
    name: 'Nehemiah',
    testament: 'old',
    chapters: 13,
    verses: 406,
  },
  {
    key: 'esther',
    name: 'Esther',
    testament: 'old',
    chapters: 10,
    verses: 167,
  },
  { key: 'job', name: 'Job', testament: 'old', chapters: 42, verses: 1070 },
  {
    key: 'psalms',
    name: 'Psalms',
    testament: 'old',
    chapters: 150,
    verses: 2461,
  },
  {
    key: 'proverbs',
    name: 'Proverbs',
    testament: 'old',
    chapters: 31,
    verses: 915,
  },
  {
    key: 'ecclesiastes',
    name: 'Ecclesiastes',
    testament: 'old',
    chapters: 12,
    verses: 222,
  },
  {
    key: 'song-of-solomon',
    name: 'Song of Solomon',
    testament: 'old',
    chapters: 8,
    verses: 117,
  },
  {
    key: 'isaiah',
    name: 'Isaiah',
    testament: 'old',
    chapters: 66,
    verses: 1292,
  },
  {
    key: 'jeremiah',
    name: 'Jeremiah',
    testament: 'old',
    chapters: 52,
    verses: 1364,
  },
  {
    key: 'lamentations',
    name: 'Lamentations',
    testament: 'old',
    chapters: 5,
    verses: 154,
  },
  {
    key: 'ezekiel',
    name: 'Ezekiel',
    testament: 'old',
    chapters: 48,
    verses: 1273,
  },
  {
    key: 'daniel',
    name: 'Daniel',
    testament: 'old',
    chapters: 12,
    verses: 357,
  },
  { key: 'hosea', name: 'Hosea', testament: 'old', chapters: 14, verses: 197 },
  { key: 'joel', name: 'Joel', testament: 'old', chapters: 3, verses: 73 },
  { key: 'amos', name: 'Amos', testament: 'old', chapters: 9, verses: 146 },
  {
    key: 'obadiah',
    name: 'Obadiah',
    testament: 'old',
    chapters: 1,
    verses: 21,
  },
  { key: 'jonah', name: 'Jonah', testament: 'old', chapters: 4, verses: 48 },
  { key: 'micah', name: 'Micah', testament: 'old', chapters: 7, verses: 105 },
  { key: 'nahum', name: 'Nahum', testament: 'old', chapters: 3, verses: 47 },
  {
    key: 'habakkuk',
    name: 'Habakkuk',
    testament: 'old',
    chapters: 3,
    verses: 56,
  },
  {
    key: 'zephaniah',
    name: 'Zephaniah',
    testament: 'old',
    chapters: 3,
    verses: 53,
  },
  { key: 'haggai', name: 'Haggai', testament: 'old', chapters: 2, verses: 38 },
  {
    key: 'zechariah',
    name: 'Zechariah',
    testament: 'old',
    chapters: 14,
    verses: 211,
  },
  {
    key: 'malachi',
    name: 'Malachi',
    testament: 'old',
    chapters: 4,
    verses: 55,
  },
  {
    key: 'matthew',
    name: 'Matthew',
    testament: 'new',
    chapters: 28,
    verses: 1071,
  },
  { key: 'mark', name: 'Mark', testament: 'new', chapters: 16, verses: 678 },
  { key: 'luke', name: 'Luke', testament: 'new', chapters: 24, verses: 1151 },
  { key: 'john', name: 'John', testament: 'new', chapters: 21, verses: 879 },
  { key: 'acts', name: 'Acts', testament: 'new', chapters: 28, verses: 1007 },
  {
    key: 'romans',
    name: 'Romans',
    testament: 'new',
    chapters: 16,
    verses: 433,
  },
  {
    key: '1-corinthians',
    name: '1 Corinthians',
    testament: 'new',
    chapters: 16,
    verses: 437,
  },
  {
    key: '2-corinthians',
    name: '2 Corinthians',
    testament: 'new',
    chapters: 13,
    verses: 257,
  },
  {
    key: 'galatians',
    name: 'Galatians',
    testament: 'new',
    chapters: 6,
    verses: 149,
  },
  {
    key: 'ephesians',
    name: 'Ephesians',
    testament: 'new',
    chapters: 6,
    verses: 155,
  },
  {
    key: 'philippians',
    name: 'Philippians',
    testament: 'new',
    chapters: 4,
    verses: 104,
  },
  {
    key: 'colossians',
    name: 'Colossians',
    testament: 'new',
    chapters: 4,
    verses: 95,
  },
  {
    key: '1-thessalonians',
    name: '1 Thessalonians',
    testament: 'new',
    chapters: 5,
    verses: 89,
  },
  {
    key: '2-thessalonians',
    name: '2 Thessalonians',
    testament: 'new',
    chapters: 3,
    verses: 47,
  },
  {
    key: '1-timothy',
    name: '1 Timothy',
    testament: 'new',
    chapters: 6,
    verses: 113,
  },
  {
    key: '2-timothy',
    name: '2 Timothy',
    testament: 'new',
    chapters: 4,
    verses: 83,
  },
  { key: 'titus', name: 'Titus', testament: 'new', chapters: 3, verses: 46 },
  {
    key: 'philemon',
    name: 'Philemon',
    testament: 'new',
    chapters: 1,
    verses: 25,
  },
  {
    key: 'hebrews',
    name: 'Hebrews',
    testament: 'new',
    chapters: 13,
    verses: 303,
  },
  { key: 'james', name: 'James', testament: 'new', chapters: 5, verses: 108 },
  {
    key: '1-peter',
    name: '1 Peter',
    testament: 'new',
    chapters: 5,
    verses: 105,
  },
  {
    key: '2-peter',
    name: '2 Peter',
    testament: 'new',
    chapters: 3,
    verses: 61,
  },
  { key: '1-john', name: '1 John', testament: 'new', chapters: 5, verses: 105 },
  { key: '2-john', name: '2 John', testament: 'new', chapters: 1, verses: 13 },
  { key: '3-john', name: '3 John', testament: 'new', chapters: 1, verses: 14 },
  { key: 'jude', name: 'Jude', testament: 'new', chapters: 1, verses: 25 },
  {
    key: 'revelation',
    name: 'Revelation',
    testament: 'new',
    chapters: 22,
    verses: 404,
  },
];

/**
 * A chronological reading plan of the Bible.
 *
 * Each entry is an object specifying the book (by its standard key, e.g., 'genesis')
 * and the chapter number (1-indexed). This list should be read sequentially.
 */
export type ChronologicalReference = {
  bookKey: string;
  chapter: number;
};

export const CHRONOLOGICAL_READING_PLAN: ChronologicalReference[] = [
  // --- PERIOD 1: BEGINNINGS AND PATRIARCHS (Genesis & Job) ---
  // Creation to the Flood
  { bookKey: 'genesis', chapter: 1 },
  { bookKey: 'genesis', chapter: 2 },
  { bookKey: 'genesis', chapter: 3 },
  { bookKey: 'genesis', chapter: 4 },
  { bookKey: 'genesis', chapter: 5 },
  { bookKey: 'genesis', chapter: 6 },
  { bookKey: 'genesis', chapter: 7 },
  { bookKey: 'genesis', chapter: 8 },
  { bookKey: 'genesis', chapter: 9 },
  { bookKey: 'genesis', chapter: 10 },
  { bookKey: 'genesis', chapter: 11 },
  // Abraham
  { bookKey: 'genesis', chapter: 12 },
  { bookKey: 'genesis', chapter: 13 },
  { bookKey: 'genesis', chapter: 14 },
  { bookKey: 'genesis', chapter: 15 },
  { bookKey: 'genesis', chapter: 16 },
  { bookKey: 'genesis', chapter: 17 },
  { bookKey: 'genesis', chapter: 18 },
  { bookKey: 'genesis', chapter: 19 },
  { bookKey: 'genesis', chapter: 20 },
  // Job (placed here due to early historical setting)
  { bookKey: 'job', chapter: 1 },
  { bookKey: 'job', chapter: 2 },
  { bookKey: 'job', chapter: 3 },
  { bookKey: 'job', chapter: 4 },
  { bookKey: 'job', chapter: 5 },
  { bookKey: 'job', chapter: 6 },
  { bookKey: 'job', chapter: 7 },
  { bookKey: 'job', chapter: 8 },
  { bookKey: 'job', chapter: 9 },
  { bookKey: 'job', chapter: 10 },
  { bookKey: 'job', chapter: 11 },
  { bookKey: 'job', chapter: 12 },
  { bookKey: 'job', chapter: 13 },
  { bookKey: 'job', chapter: 14 },
  { bookKey: 'job', chapter: 15 },
  { bookKey: 'job', chapter: 16 },
  { bookKey: 'job', chapter: 17 },
  { bookKey: 'job', chapter: 18 },
  { bookKey: 'job', chapter: 19 },
  { bookKey: 'job', chapter: 20 },
  { bookKey: 'job', chapter: 21 },
  { bookKey: 'job', chapter: 22 },
  { bookKey: 'job', chapter: 23 },
  { bookKey: 'job', chapter: 24 },
  { bookKey: 'job', chapter: 25 },
  { bookKey: 'job', chapter: 26 },
  { bookKey: 'job', chapter: 27 },
  { bookKey: 'job', chapter: 28 },
  { bookKey: 'job', chapter: 29 },
  { bookKey: 'job', chapter: 30 },
  { bookKey: 'job', chapter: 31 },
  { bookKey: 'job', chapter: 32 },
  { bookKey: 'job', chapter: 33 },
  { bookKey: 'job', chapter: 34 },
  { bookKey: 'job', chapter: 35 },
  { bookKey: 'job', chapter: 36 },
  { bookKey: 'job', chapter: 37 },
  { bookKey: 'job', chapter: 38 },
  { bookKey: 'job', chapter: 39 },
  { bookKey: 'job', chapter: 40 },
  { bookKey: 'job', chapter: 41 },
  { bookKey: 'job', chapter: 42 },
  // Isaac, Jacob, Joseph
  { bookKey: 'genesis', chapter: 21 },
  { bookKey: 'genesis', chapter: 22 },
  { bookKey: 'genesis', chapter: 23 },
  { bookKey: 'genesis', chapter: 24 },
  { bookKey: 'genesis', chapter: 25 },
  { bookKey: 'genesis', chapter: 26 },
  { bookKey: 'genesis', chapter: 27 },
  { bookKey: 'genesis', chapter: 28 },
  { bookKey: 'genesis', chapter: 29 },
  { bookKey: 'genesis', chapter: 30 },
  { bookKey: 'genesis', chapter: 31 },
  { bookKey: 'genesis', chapter: 32 },
  { bookKey: 'genesis', chapter: 33 },
  { bookKey: 'genesis', chapter: 34 },
  { bookKey: 'genesis', chapter: 35 },
  { bookKey: 'genesis', chapter: 36 },
  { bookKey: 'genesis', chapter: 37 },
  { bookKey: 'genesis', chapter: 38 },
  { bookKey: 'genesis', chapter: 39 },
  { bookKey: 'genesis', chapter: 40 },
  { bookKey: 'genesis', chapter: 41 },
  { bookKey: 'genesis', chapter: 42 },
  { bookKey: 'genesis', chapter: 43 },
  { bookKey: 'genesis', chapter: 44 },
  { bookKey: 'genesis', chapter: 45 },
  { bookKey: 'genesis', chapter: 46 },
  { bookKey: 'genesis', chapter: 47 },
  { bookKey: 'genesis', chapter: 48 },
  { bookKey: 'genesis', chapter: 49 },
  { bookKey: 'genesis', chapter: 50 },

  // --- PERIOD 2: EXODUS, LAW, AND WILDERNESS (Exodus - Deuteronomy) ---
  // Moses and the Exodus
  { bookKey: 'exodus', chapter: 1 },
  { bookKey: 'exodus', chapter: 2 },
  { bookKey: 'exodus', chapter: 3 },
  { bookKey: 'exodus', chapter: 4 },
  { bookKey: 'exodus', chapter: 5 },
  { bookKey: 'exodus', chapter: 6 },
  { bookKey: 'exodus', chapter: 7 },
  { bookKey: 'exodus', chapter: 8 },
  { bookKey: 'exodus', chapter: 9 },
  { bookKey: 'exodus', chapter: 10 },
  { bookKey: 'exodus', chapter: 11 },
  { bookKey: 'exodus', chapter: 12 },
  { bookKey: 'exodus', chapter: 13 },
  { bookKey: 'exodus', chapter: 14 },
  { bookKey: 'exodus', chapter: 15 },
  { bookKey: 'exodus', chapter: 16 },
  { bookKey: 'exodus', chapter: 17 },
  { bookKey: 'exodus', chapter: 18 },
  { bookKey: 'exodus', chapter: 19 },
  { bookKey: 'exodus', chapter: 20 },
  // Law and Tabernacle Instructions
  { bookKey: 'exodus', chapter: 21 },
  { bookKey: 'exodus', chapter: 22 },
  { bookKey: 'exodus', chapter: 23 },
  { bookKey: 'exodus', chapter: 24 },
  { bookKey: 'exodus', chapter: 25 },
  { bookKey: 'exodus', chapter: 26 },
  { bookKey: 'exodus', chapter: 27 },
  { bookKey: 'exodus', chapter: 28 },
  { bookKey: 'exodus', chapter: 29 },
  { bookKey: 'exodus', chapter: 30 },
  { bookKey: 'exodus', chapter: 31 },
  { bookKey: 'exodus', chapter: 32 },
  { bookKey: 'exodus', chapter: 33 },
  { bookKey: 'exodus', chapter: 34 },
  { bookKey: 'exodus', chapter: 35 },
  { bookKey: 'exodus', chapter: 36 },
  { bookKey: 'exodus', chapter: 37 },
  { bookKey: 'exodus', chapter: 38 },
  { bookKey: 'exodus', chapter: 39 },
  { bookKey: 'exodus', chapter: 40 },
  // Leviticus (Year spent at Mount Sinai)
  { bookKey: 'leviticus', chapter: 1 },
  { bookKey: 'leviticus', chapter: 2 },
  { bookKey: 'leviticus', chapter: 3 },
  { bookKey: 'leviticus', chapter: 4 },
  { bookKey: 'leviticus', chapter: 5 },
  { bookKey: 'leviticus', chapter: 6 },
  { bookKey: 'leviticus', chapter: 7 },
  { bookKey: 'leviticus', chapter: 8 },
  { bookKey: 'leviticus', chapter: 9 },
  { bookKey: 'leviticus', chapter: 10 },
  { bookKey: 'leviticus', chapter: 11 },
  { bookKey: 'leviticus', chapter: 12 },
  { bookKey: 'leviticus', chapter: 13 },
  { bookKey: 'leviticus', chapter: 14 },
  { bookKey: 'leviticus', chapter: 15 },
  { bookKey: 'leviticus', chapter: 16 },
  { bookKey: 'leviticus', chapter: 17 },
  { bookKey: 'leviticus', chapter: 18 },
  { bookKey: 'leviticus', chapter: 19 },
  { bookKey: 'leviticus', chapter: 20 },
  { bookKey: 'leviticus', chapter: 21 },
  { bookKey: 'leviticus', chapter: 22 },
  { bookKey: 'leviticus', chapter: 23 },
  { bookKey: 'leviticus', chapter: 24 },
  { bookKey: 'leviticus', chapter: 25 },
  { bookKey: 'leviticus', chapter: 26 },
  { bookKey: 'leviticus', chapter: 27 },
  // Numbers (Wandering in the Wilderness)
  { bookKey: 'numbers', chapter: 1 },
  { bookKey: 'numbers', chapter: 2 },
  { bookKey: 'numbers', chapter: 3 },
  { bookKey: 'numbers', chapter: 4 },
  { bookKey: 'numbers', chapter: 5 },
  { bookKey: 'numbers', chapter: 6 },
  { bookKey: 'numbers', chapter: 7 },
  { bookKey: 'numbers', chapter: 8 },
  { bookKey: 'numbers', chapter: 9 },
  { bookKey: 'numbers', chapter: 10 },
  { bookKey: 'numbers', chapter: 11 },
  { bookKey: 'numbers', chapter: 12 },
  { bookKey: 'numbers', chapter: 13 },
  { bookKey: 'numbers', chapter: 14 },
  { bookKey: 'numbers', chapter: 15 },
  { bookKey: 'numbers', chapter: 16 },
  { bookKey: 'numbers', chapter: 17 },
  { bookKey: 'numbers', chapter: 18 },
  { bookKey: 'numbers', chapter: 19 },
  { bookKey: 'numbers', chapter: 20 },
  { bookKey: 'numbers', chapter: 21 },
  { bookKey: 'numbers', chapter: 22 },
  { bookKey: 'numbers', chapter: 23 },
  { bookKey: 'numbers', chapter: 24 },
  { bookKey: 'numbers', chapter: 25 },
  { bookKey: 'numbers', chapter: 26 },
  { bookKey: 'numbers', chapter: 27 },
  { bookKey: 'numbers', chapter: 28 },
  { bookKey: 'numbers', chapter: 29 },
  { bookKey: 'numbers', chapter: 30 },
  { bookKey: 'numbers', chapter: 31 },
  { bookKey: 'numbers', chapter: 32 },
  { bookKey: 'numbers', chapter: 33 },
  { bookKey: 'numbers', chapter: 34 },
  { bookKey: 'numbers', chapter: 35 },
  { bookKey: 'numbers', chapter: 36 },
  // Deuteronomy (Moses' Farewell Messages)
  { bookKey: 'deuteronomy', chapter: 1 },
  { bookKey: 'deuteronomy', chapter: 2 },
  { bookKey: 'deuteronomy', chapter: 3 },
  { bookKey: 'deuteronomy', chapter: 4 },
  { bookKey: 'deuteronomy', chapter: 5 },
  { bookKey: 'deuteronomy', chapter: 6 },
  { bookKey: 'deuteronomy', chapter: 7 },
  { bookKey: 'deuteronomy', chapter: 8 },
  { bookKey: 'deuteronomy', chapter: 9 },
  { bookKey: 'deuteronomy', chapter: 10 },
  { bookKey: 'deuteronomy', chapter: 11 },
  { bookKey: 'deuteronomy', chapter: 12 },
  { bookKey: 'deuteronomy', chapter: 13 },
  { bookKey: 'deuteronomy', chapter: 14 },
  { bookKey: 'deuteronomy', chapter: 15 },
  { bookKey: 'deuteronomy', chapter: 16 },
  { bookKey: 'deuteronomy', chapter: 17 },
  { bookKey: 'deuteronomy', chapter: 18 },
  { bookKey: 'deuteronomy', chapter: 19 },
  { bookKey: 'deuteronomy', chapter: 20 },
  { bookKey: 'deuteronomy', chapter: 21 },
  { bookKey: 'deuteronomy', chapter: 22 },
  { bookKey: 'deuteronomy', chapter: 23 },
  { bookKey: 'deuteronomy', chapter: 24 },
  { bookKey: 'deuteronomy', chapter: 25 },
  { bookKey: 'deuteronomy', chapter: 26 },
  { bookKey: 'deuteronomy', chapter: 27 },
  { bookKey: 'deuteronomy', chapter: 28 },
  { bookKey: 'deuteronomy', chapter: 29 },
  { bookKey: 'deuteronomy', chapter: 30 },
  { bookKey: 'deuteronomy', chapter: 31 },
  { bookKey: 'deuteronomy', chapter: 32 },
  { bookKey: 'deuteronomy', chapter: 33 },
  { bookKey: 'deuteronomy', chapter: 34 },

  // --- PERIOD 3: CONQUEST AND JUDGES (Joshua - 2 Samuel) ---
  // Conquest of Canaan
  { bookKey: 'joshua', chapter: 1 },
  { bookKey: 'joshua', chapter: 2 },
  { bookKey: 'joshua', chapter: 3 },
  { bookKey: 'joshua', chapter: 4 },
  { bookKey: 'joshua', chapter: 5 },
  { bookKey: 'joshua', chapter: 6 },
  { bookKey: 'joshua', chapter: 7 },
  { bookKey: 'joshua', chapter: 8 },
  { bookKey: 'joshua', chapter: 9 },
  { bookKey: 'joshua', chapter: 10 },
  { bookKey: 'joshua', chapter: 11 },
  { bookKey: 'joshua', chapter: 12 },
  { bookKey: 'joshua', chapter: 13 },
  { bookKey: 'joshua', chapter: 14 },
  { bookKey: 'joshua', chapter: 15 },
  { bookKey: 'joshua', chapter: 16 },
  { bookKey: 'joshua', chapter: 17 },
  { bookKey: 'joshua', chapter: 18 },
  { bookKey: 'joshua', chapter: 19 },
  { bookKey: 'joshua', chapter: 20 },
  { bookKey: 'joshua', chapter: 21 },
  { bookKey: 'joshua', chapter: 22 },
  { bookKey: 'joshua', chapter: 23 },
  { bookKey: 'joshua', chapter: 24 },
  // Period of the Judges
  { bookKey: 'judges', chapter: 1 },
  { bookKey: 'judges', chapter: 2 },
  { bookKey: 'judges', chapter: 3 },
  { bookKey: 'judges', chapter: 4 },
  { bookKey: 'judges', chapter: 5 },
  { bookKey: 'judges', chapter: 6 },
  { bookKey: 'judges', chapter: 7 },
  { bookKey: 'judges', chapter: 8 },
  { bookKey: 'judges', chapter: 9 },
  { bookKey: 'judges', chapter: 10 },
  { bookKey: 'judges', chapter: 11 },
  { bookKey: 'judges', chapter: 12 },
  { bookKey: 'judges', chapter: 13 },
  { bookKey: 'judges', chapter: 14 },
  { bookKey: 'judges', chapter: 15 },
  { bookKey: 'judges', chapter: 16 },
  { bookKey: 'judges', chapter: 17 },
  { bookKey: 'judges', chapter: 18 },
  { bookKey: 'judges', chapter: 19 },
  { bookKey: 'judges', chapter: 20 },
  { bookKey: 'judges', chapter: 21 },
  { bookKey: 'ruth', chapter: 1 }, // Ruth is often placed during the Judges period
  { bookKey: 'ruth', chapter: 2 },
  { bookKey: 'ruth', chapter: 3 },
  { bookKey: 'ruth', chapter: 4 },
  // United Kingdom: Saul
  { bookKey: '1-samuel', chapter: 1 },
  { bookKey: '1-samuel', chapter: 2 },
  { bookKey: '1-samuel', chapter: 3 },
  { bookKey: '1-samuel', chapter: 4 },
  { bookKey: '1-samuel', chapter: 5 },
  { bookKey: '1-samuel', chapter: 6 },
  { bookKey: '1-samuel', chapter: 7 },
  { bookKey: '1-samuel', chapter: 8 },
  { bookKey: '1-samuel', chapter: 9 },
  { bookKey: '1-samuel', chapter: 10 },
  { bookKey: '1-samuel', chapter: 11 },
  { bookKey: '1-samuel', chapter: 12 },
  { bookKey: '1-samuel', chapter: 13 },
  { bookKey: '1-samuel', chapter: 14 },
  { bookKey: '1-samuel', chapter: 15 },
  { bookKey: '1-samuel', chapter: 16 },
  { bookKey: '1-samuel', chapter: 17 },
  { bookKey: '1-samuel', chapter: 18 },
  { bookKey: '1-samuel', chapter: 19 },
  { bookKey: '1-samuel', chapter: 20 },
  { bookKey: 'psalms', chapter: 11 }, // Psalms interwoven with David's life
  { bookKey: 'psalms', chapter: 59 },
  { bookKey: 'psalms', chapter: 7 },
  { bookKey: 'psalms', chapter: 27 },
  { bookKey: 'psalms', chapter: 31 },
  { bookKey: 'psalms', chapter: 34 },
  { bookKey: 'psalms', chapter: 52 },
  { bookKey: 'psalms', chapter: 58 },
  { bookKey: 'psalms', chapter: 142 },
  { bookKey: '1-samuel', chapter: 21 },
  { bookKey: '1-samuel', chapter: 22 },
  { bookKey: 'psalms', chapter: 17 },
  { bookKey: 'psalms', chapter: 35 },
  { bookKey: 'psalms', chapter: 64 },
  { bookKey: 'psalms', chapter: 140 },
  { bookKey: '1-samuel', chapter: 23 },
  { bookKey: 'psalms', chapter: 54 },
  { bookKey: '1-samuel', chapter: 24 },
  { bookKey: '1-samuel', chapter: 25 },
  { bookKey: '1-samuel', chapter: 26 },
  { bookKey: '1-samuel', chapter: 27 },
  { bookKey: 'psalms', chapter: 120 },
  { bookKey: '1-samuel', chapter: 28 },
  { bookKey: '1-samuel', chapter: 29 },
  { bookKey: '1-samuel', chapter: 30 },
  { bookKey: '1-samuel', chapter: 31 },
  { bookKey: 'psalms', chapter: 1 },
  { bookKey: 'psalms', chapter: 2 },
  { bookKey: 'psalms', chapter: 43 },
  // United Kingdom: David
  { bookKey: '2-samuel', chapter: 1 },
  { bookKey: '2-samuel', chapter: 2 },
  { bookKey: '2-samuel', chapter: 3 },
  { bookKey: '2-samuel', chapter: 4 },
  { bookKey: '2-samuel', chapter: 5 },
  { bookKey: '1-chronicles', chapter: 1 }, // Historical Summary - David's reign starts
  { bookKey: '1-chronicles', chapter: 2 },
  { bookKey: '1-chronicles', chapter: 3 },
  { bookKey: '1-chronicles', chapter: 4 },
  { bookKey: '1-chronicles', chapter: 5 },
  { bookKey: '1-chronicles', chapter: 6 },
  { bookKey: '1-chronicles', chapter: 7 },
  { bookKey: '1-chronicles', chapter: 8 },
  { bookKey: '1-chronicles', chapter: 9 },
  { bookKey: '1-chronicles', chapter: 10 },
  { bookKey: '1-chronicles', chapter: 11 },
  { bookKey: '1-chronicles', chapter: 12 },
  { bookKey: 'psalms', chapter: 133 },
  { bookKey: '2-samuel', chapter: 6 },
  { bookKey: '1-chronicles', chapter: 13 },
  { bookKey: '1-chronicles', chapter: 14 },
  { bookKey: '1-chronicles', chapter: 15 },
  { bookKey: '1-chronicles', chapter: 16 },
  { bookKey: 'psalms', chapter: 68 },
  { bookKey: 'psalms', chapter: 106 },
  { bookKey: '2-samuel', chapter: 7 },
  { bookKey: '1-chronicles', chapter: 17 },
  { bookKey: '2-samuel', chapter: 8 },
  { bookKey: '1-chronicles', chapter: 18 },
  { bookKey: 'psalms', chapter: 89 },
  { bookKey: '2-samuel', chapter: 9 },
  { bookKey: '2-samuel', chapter: 10 },
  { bookKey: '1-chronicles', chapter: 19 },
  { bookKey: '2-samuel', chapter: 11 },
  { bookKey: '2-samuel', chapter: 12 },
  { bookKey: '1-chronicles', chapter: 20 },
  { bookKey: 'psalms', chapter: 32 },
  { bookKey: 'psalms', chapter: 51 },
  { bookKey: 'psalms', chapter: 90 },
  { bookKey: 'psalms', chapter: 91 },
  { bookKey: '2-samuel', chapter: 13 },
  { bookKey: '2-samuel', chapter: 14 },
  { bookKey: '2-samuel', chapter: 15 },
  { bookKey: 'psalms', chapter: 3 },
  { bookKey: 'psalms', chapter: 4 },
  { bookKey: 'psalms', chapter: 12 },
  { bookKey: 'psalms', chapter: 28 },
  { bookKey: 'psalms', chapter: 55 },
  { bookKey: '2-samuel', chapter: 16 },
  { bookKey: '2-samuel', chapter: 17 },
  { bookKey: 'psalms', chapter: 23 },
  { bookKey: '2-samuel', chapter: 18 },
  { bookKey: 'psalms', chapter: 20 },
  { bookKey: 'psalms', chapter: 21 },
  { bookKey: '2-samuel', chapter: 19 },
  { bookKey: '2-samuel', chapter: 20 },
  { bookKey: '2-samuel', chapter: 21 },
  { bookKey: '2-samuel', chapter: 22 },
  { bookKey: 'psalms', chapter: 57 },
  { bookKey: 'psalms', chapter: 108 },
  { bookKey: '2-samuel', chapter: 23 },
  { bookKey: '2-samuel', chapter: 24 },
  { bookKey: '1-chronicles', chapter: 21 },
  { bookKey: '1-chronicles', chapter: 22 },
  { bookKey: '1-chronicles', chapter: 23 },
  { bookKey: 'psalms', chapter: 131 },
  { bookKey: '1-chronicles', chapter: 24 },
  { bookKey: '1-chronicles', chapter: 25 },
  { bookKey: '1-chronicles', chapter: 26 },
  { bookKey: '1-chronicles', chapter: 27 },
  { bookKey: '1-chronicles', chapter: 28 },
  { bookKey: '1-chronicles', chapter: 29 },
  { bookKey: 'psalms', chapter: 92 },
  { bookKey: 'psalms', chapter: 93 },
  // United Kingdom: Solomon
  { bookKey: '1-kings', chapter: 1 },
  { bookKey: '1-kings', chapter: 2 },
  { bookKey: 'psalms', chapter: 138 },
  { bookKey: 'psalms', chapter: 139 },
  { bookKey: 'psalms', chapter: 143 },
  { bookKey: '1-kings', chapter: 3 },
  { bookKey: '2-chronicles', chapter: 1 },
  { bookKey: '1-kings', chapter: 4 },
  { bookKey: 'psalms', chapter: 72 },
  { bookKey: '1-kings', chapter: 5 },
  { bookKey: '1-kings', chapter: 6 },
  { bookKey: '2-chronicles', chapter: 2 },
  { bookKey: '2-chronicles', chapter: 3 },
  { bookKey: '2-chronicles', chapter: 4 },
  { bookKey: '1-kings', chapter: 7 },
  { bookKey: '1-kings', chapter: 8 },
  { bookKey: '2-chronicles', chapter: 5 },
  { bookKey: '2-chronicles', chapter: 6 },
  { bookKey: '2-chronicles', chapter: 7 },
  { bookKey: 'psalms', chapter: 136 },
  { bookKey: '1-kings', chapter: 9 },
  { bookKey: '1-kings', chapter: 10 },
  { bookKey: '2-chronicles', chapter: 8 },
  { bookKey: '2-chronicles', chapter: 9 },
  { bookKey: 'proverbs', chapter: 1 },
  { bookKey: 'proverbs', chapter: 2 },
  { bookKey: 'proverbs', chapter: 3 },
  { bookKey: 'proverbs', chapter: 4 },
  { bookKey: 'proverbs', chapter: 5 },
  { bookKey: 'proverbs', chapter: 6 },
  { bookKey: 'proverbs', chapter: 7 },
  { bookKey: 'proverbs', chapter: 8 },
  { bookKey: 'proverbs', chapter: 9 },
  { bookKey: 'proverbs', chapter: 10 },
  { bookKey: 'proverbs', chapter: 11 },
  { bookKey: 'proverbs', chapter: 12 },
  { bookKey: 'proverbs', chapter: 13 },
  { bookKey: 'proverbs', chapter: 14 },
  { bookKey: 'proverbs', chapter: 15 },
  { bookKey: 'proverbs', chapter: 16 },
  { bookKey: 'proverbs', chapter: 17 },
  { bookKey: 'proverbs', chapter: 18 },
  { bookKey: 'proverbs', chapter: 19 },
  { bookKey: 'proverbs', chapter: 20 },
  { bookKey: 'proverbs', chapter: 21 },
  { bookKey: 'proverbs', chapter: 22 },
  { bookKey: 'proverbs', chapter: 23 },
  { bookKey: 'proverbs', chapter: 24 },
  { bookKey: 'proverbs', chapter: 25 },
  { bookKey: 'proverbs', chapter: 26 },
  { bookKey: 'proverbs', chapter: 27 },
  { bookKey: 'proverbs', chapter: 28 },
  { bookKey: 'proverbs', chapter: 29 },
  { bookKey: 'proverbs', chapter: 30 },
  { bookKey: 'proverbs', chapter: 31 },
  { bookKey: 'ecclesiastes', chapter: 1 },
  { bookKey: 'ecclesiastes', chapter: 2 },
  { bookKey: 'ecclesiastes', chapter: 3 },
  { bookKey: 'ecclesiastes', chapter: 4 },
  { bookKey: 'ecclesiastes', chapter: 5 },
  { bookKey: 'ecclesiastes', chapter: 6 },
  { bookKey: 'ecclesiastes', chapter: 7 },
  { bookKey: 'ecclesiastes', chapter: 8 },
  { bookKey: 'ecclesiastes', chapter: 9 },
  { bookKey: 'ecclesiastes', chapter: 10 },
  { bookKey: 'ecclesiastes', chapter: 11 },
  { bookKey: 'ecclesiastes', chapter: 12 },
  { bookKey: 'song-of-solomon', chapter: 1 },
  { bookKey: 'song-of-solomon', chapter: 2 },
  { bookKey: 'song-of-solomon', chapter: 3 },
  { bookKey: 'song-of-solomon', chapter: 4 },
  { bookKey: 'song-of-solomon', chapter: 5 },
  { bookKey: 'song-of-solomon', chapter: 6 },
  { bookKey: 'song-of-solomon', chapter: 7 },
  { bookKey: 'song-of-solomon', chapter: 8 },
  { bookKey: '1-kings', chapter: 11 },

  // --- PERIOD 4: DIVIDED KINGDOM (1 Kings - 2 Kings & Prophets) ---
  { bookKey: '1-kings', chapter: 12 },
  { bookKey: '2-chronicles', chapter: 10 },
  { bookKey: '1-kings', chapter: 13 },
  { bookKey: '1-kings', chapter: 14 },
  { bookKey: '2-chronicles', chapter: 11 },
  { bookKey: '2-chronicles', chapter: 12 },
  { bookKey: '1-kings', chapter: 15 },
  { bookKey: '2-chronicles', chapter: 13 },
  { bookKey: '2-chronicles', chapter: 14 },
  { bookKey: '2-chronicles', chapter: 15 },
  { bookKey: '2-chronicles', chapter: 16 },
  { bookKey: '1-kings', chapter: 16 },
  { bookKey: '1-kings', chapter: 17 },
  { bookKey: '1-kings', chapter: 18 },
  { bookKey: '1-kings', chapter: 19 },
  { bookKey: '1-kings', chapter: 20 },
  { bookKey: '1-kings', chapter: 21 },
  { bookKey: '1-kings', chapter: 22 },
  { bookKey: '2-chronicles', chapter: 17 },
  { bookKey: '2-chronicles', chapter: 18 },
  { bookKey: '2-chronicles', chapter: 19 },
  { bookKey: '2-chronicles', chapter: 20 },
  { bookKey: 'obadiah', chapter: 1 }, // Prophecy (timing uncertain, often placed here)
  { bookKey: '2-kings', chapter: 1 },
  { bookKey: '2-kings', chapter: 2 },
  { bookKey: '2-kings', chapter: 3 },
  { bookKey: '2-kings', chapter: 4 },
  { bookKey: '2-kings', chapter: 5 },
  { bookKey: '2-kings', chapter: 6 },
  { bookKey: '2-kings', chapter: 7 },
  { bookKey: '2-kings', chapter: 8 },
  { bookKey: '2-chronicles', chapter: 21 },
  { bookKey: '2-chronicles', chapter: 22 },
  { bookKey: '2-kings', chapter: 9 },
  { bookKey: '2-kings', chapter: 10 },
  { bookKey: '2-kings', chapter: 11 },
  { bookKey: '2-chronicles', chapter: 23 },
  { bookKey: '2-kings', chapter: 12 },
  { bookKey: '2-chronicles', chapter: 24 },
  { bookKey: 'joel', chapter: 1 }, // Prophecy
  { bookKey: 'joel', chapter: 2 },
  { bookKey: 'joel', chapter: 3 },
  { bookKey: '2-kings', chapter: 13 },
  { bookKey: '2-kings', chapter: 14 },
  { bookKey: '2-chronicles', chapter: 25 },
  { bookKey: 'jonah', chapter: 1 }, // Prophecy
  { bookKey: 'jonah', chapter: 2 },
  { bookKey: 'jonah', chapter: 3 },
  { bookKey: 'jonah', chapter: 4 },
  { bookKey: 'amos', chapter: 1 }, // Prophecy
  { bookKey: 'amos', chapter: 2 },
  { bookKey: 'amos', chapter: 3 },
  { bookKey: 'amos', chapter: 4 },
  { bookKey: 'amos', chapter: 5 },
  { bookKey: 'amos', chapter: 6 },
  { bookKey: 'amos', chapter: 7 },
  { bookKey: 'amos', chapter: 8 },
  { bookKey: 'amos', chapter: 9 },
  { bookKey: '2-kings', chapter: 15 },
  { bookKey: '2-chronicles', chapter: 26 },
  { bookKey: 'hosea', chapter: 1 }, // Prophecy
  { bookKey: 'hosea', chapter: 2 },
  { bookKey: 'hosea', chapter: 3 },
  { bookKey: 'hosea', chapter: 4 },
  { bookKey: 'hosea', chapter: 5 },
  { bookKey: 'hosea', chapter: 6 },
  { bookKey: 'hosea', chapter: 7 },
  { bookKey: 'hosea', chapter: 8 },
  { bookKey: 'hosea', chapter: 9 },
  { bookKey: 'hosea', chapter: 10 },
  { bookKey: 'hosea', chapter: 11 },
  { bookKey: 'hosea', chapter: 12 },
  { bookKey: 'hosea', chapter: 13 },
  { bookKey: 'hosea', chapter: 14 },
  { bookKey: 'isaiah', chapter: 1 }, // Prophecy (early chapters)
  { bookKey: 'isaiah', chapter: 2 },
  { bookKey: 'isaiah', chapter: 3 },
  { bookKey: 'isaiah', chapter: 4 },
  { bookKey: 'isaiah', chapter: 5 },
  { bookKey: 'micah', chapter: 1 }, // Prophecy
  { bookKey: 'micah', chapter: 2 },
  { bookKey: 'micah', chapter: 3 },
  { bookKey: 'micah', chapter: 4 },
  { bookKey: 'micah', chapter: 5 },
  { bookKey: 'micah', chapter: 6 },
  { bookKey: 'micah', chapter: 7 },
  { bookKey: '2-kings', chapter: 16 },
  { bookKey: '2-chronicles', chapter: 27 },
  { bookKey: 'isaiah', chapter: 6 },
  { bookKey: 'isaiah', chapter: 7 },
  { bookKey: 'isaiah', chapter: 8 },
  { bookKey: 'isaiah', chapter: 9 },
  { bookKey: 'isaiah', chapter: 10 },
  { bookKey: 'isaiah', chapter: 11 },
  { bookKey: 'isaiah', chapter: 12 },
  { bookKey: 'isaiah', chapter: 13 },
  { bookKey: 'isaiah', chapter: 14 },
  { bookKey: 'isaiah', chapter: 15 },
  { bookKey: 'isaiah', chapter: 16 },
  { bookKey: 'isaiah', chapter: 17 },
  { bookKey: 'isaiah', chapter: 18 },
  { bookKey: 'isaiah', chapter: 19 },
  { bookKey: 'isaiah', chapter: 20 },
  { bookKey: 'isaiah', chapter: 21 },
  { bookKey: 'isaiah', chapter: 22 },
  { bookKey: 'isaiah', chapter: 23 },
  { bookKey: '2-kings', chapter: 17 },
  { bookKey: '2-kings', chapter: 18 },
  { bookKey: '2-chronicles', chapter: 28 },
  { bookKey: '2-chronicles', chapter: 29 },
  { bookKey: '2-chronicles', chapter: 30 },
  { bookKey: '2-chronicles', chapter: 31 },
  { bookKey: 'isaiah', chapter: 24 },
  { bookKey: 'isaiah', chapter: 25 },
  { bookKey: 'isaiah', chapter: 26 },
  { bookKey: 'isaiah', chapter: 27 },
  { bookKey: 'isaiah', chapter: 28 },
  { bookKey: 'isaiah', chapter: 29 },
  { bookKey: 'isaiah', chapter: 30 },
  { bookKey: 'isaiah', chapter: 31 },
  { bookKey: 'isaiah', chapter: 32 },
  { bookKey: 'isaiah', chapter: 33 },
  { bookKey: 'isaiah', chapter: 34 },
  { bookKey: 'isaiah', chapter: 35 },
  { bookKey: '2-kings', chapter: 19 },
  { bookKey: 'isaiah', chapter: 36 },
  { bookKey: 'isaiah', chapter: 37 },
  { bookKey: 'isaiah', chapter: 38 },
  { bookKey: 'isaiah', chapter: 39 },
  { bookKey: '2-kings', chapter: 20 },
  { bookKey: 'nahum', chapter: 1 }, // Prophecy
  { bookKey: 'nahum', chapter: 2 },
  { bookKey: 'nahum', chapter: 3 },
  { bookKey: '2-kings', chapter: 21 },
  { bookKey: '2-chronicles', chapter: 32 },
  { bookKey: '2-chronicles', chapter: 33 },
  { bookKey: '2-kings', chapter: 22 },
  { bookKey: '2-chronicles', chapter: 34 },
  { bookKey: 'zephaniah', chapter: 1 }, // Prophecy
  { bookKey: 'zephaniah', chapter: 2 },
  { bookKey: 'zephaniah', chapter: 3 },
  { bookKey: 'jeremiah', chapter: 1 }, // Prophecy
  { bookKey: 'jeremiah', chapter: 2 },
  { bookKey: 'jeremiah', chapter: 3 },
  { bookKey: 'jeremiah', chapter: 4 },
  { bookKey: 'jeremiah', chapter: 5 },
  { bookKey: 'jeremiah', chapter: 6 },
  { bookKey: 'jeremiah', chapter: 7 },
  { bookKey: 'jeremiah', chapter: 8 },
  { bookKey: 'jeremiah', chapter: 9 },
  { bookKey: 'jeremiah', chapter: 10 },
  { bookKey: 'jeremiah', chapter: 11 },
  { bookKey: 'jeremiah', chapter: 12 },
  { bookKey: '2-kings', chapter: 23 },
  { bookKey: '2-chronicles', chapter: 35 },
  { bookKey: 'jeremiah', chapter: 13 },
  { bookKey: 'jeremiah', chapter: 14 },
  { bookKey: 'jeremiah', chapter: 15 },
  { bookKey: 'jeremiah', chapter: 16 },
  { bookKey: 'jeremiah', chapter: 17 },
  { bookKey: 'jeremiah', chapter: 18 },
  { bookKey: 'jeremiah', chapter: 19 },
  { bookKey: 'jeremiah', chapter: 20 },
  { bookKey: 'habakkuk', chapter: 1 }, // Prophecy
  { bookKey: 'habakkuk', chapter: 2 },
  { bookKey: 'habakkuk', chapter: 3 },
  { bookKey: '2-kings', chapter: 24 },
  { bookKey: '2-chronicles', chapter: 36 },
  { bookKey: 'jeremiah', chapter: 21 },
  { bookKey: 'jeremiah', chapter: 22 },
  { bookKey: 'jeremiah', chapter: 23 },
  { bookKey: 'jeremiah', chapter: 24 },
  { bookKey: 'jeremiah', chapter: 25 },
  { bookKey: 'jeremiah', chapter: 26 },
  { bookKey: 'jeremiah', chapter: 27 },
  { bookKey: 'jeremiah', chapter: 28 },
  { bookKey: 'jeremiah', chapter: 29 },
  { bookKey: 'jeremiah', chapter: 30 },
  { bookKey: 'jeremiah', chapter: 31 },
  { bookKey: 'jeremiah', chapter: 32 },
  { bookKey: 'jeremiah', chapter: 33 },
  { bookKey: 'jeremiah', chapter: 34 },
  { bookKey: 'jeremiah', chapter: 35 },
  { bookKey: 'jeremiah', chapter: 36 },
  { bookKey: 'jeremiah', chapter: 37 },
  { bookKey: 'jeremiah', chapter: 38 },
  { bookKey: 'jeremiah', chapter: 39 },
  { bookKey: 'jeremiah', chapter: 40 },
  { bookKey: 'jeremiah', chapter: 41 },
  { bookKey: 'jeremiah', chapter: 42 },
  { bookKey: 'jeremiah', chapter: 43 },
  { bookKey: 'jeremiah', chapter: 44 },
  { bookKey: 'jeremiah', chapter: 45 },
  { bookKey: '2-kings', chapter: 25 },
  { bookKey: 'lamentations', chapter: 1 }, // Lamentations (after Jerusalem's fall)
  { bookKey: 'lamentations', chapter: 2 },
  { bookKey: 'lamentations', chapter: 3 },
  { bookKey: 'lamentations', chapter: 4 },
  { bookKey: 'lamentations', chapter: 5 },

  // --- PERIOD 5: EXILE AND RETURN (Daniel - Malachi) ---
  // Exile (Babylon)
  { bookKey: 'ezekiel', chapter: 1 },
  { bookKey: 'ezekiel', chapter: 2 },
  { bookKey: 'ezekiel', chapter: 3 },
  { bookKey: 'ezekiel', chapter: 4 },
  { bookKey: 'ezekiel', chapter: 5 },
  { bookKey: 'ezekiel', chapter: 6 },
  { bookKey: 'ezekiel', chapter: 7 },
  { bookKey: 'ezekiel', chapter: 8 },
  { bookKey: 'ezekiel', chapter: 9 },
  { bookKey: 'ezekiel', chapter: 10 },
  { bookKey: 'ezekiel', chapter: 11 },
  { bookKey: 'daniel', chapter: 1 },
  { bookKey: 'daniel', chapter: 2 },
  { bookKey: 'daniel', chapter: 3 },
  { bookKey: 'daniel', chapter: 4 },
  { bookKey: 'daniel', chapter: 5 },
  { bookKey: 'daniel', chapter: 6 },
  { bookKey: 'ezekiel', chapter: 12 },
  { bookKey: 'ezekiel', chapter: 13 },
  { bookKey: 'ezekiel', chapter: 14 },
  { bookKey: 'ezekiel', chapter: 15 },
  { bookKey: 'ezekiel', chapter: 16 },
  { bookKey: 'ezekiel', chapter: 17 },
  { bookKey: 'ezekiel', chapter: 18 },
  { bookKey: 'ezekiel', chapter: 19 },
  { bookKey: 'ezekiel', chapter: 20 },
  { bookKey: 'ezekiel', chapter: 21 },
  { bookKey: 'ezekiel', chapter: 22 },
  { bookKey: 'ezekiel', chapter: 23 },
  { bookKey: 'ezekiel', chapter: 24 },
  { bookKey: 'ezekiel', chapter: 25 },
  { bookKey: 'ezekiel', chapter: 26 },
  { bookKey: 'ezekiel', chapter: 27 },
  { bookKey: 'ezekiel', chapter: 28 },
  { bookKey: 'ezekiel', chapter: 29 },
  { bookKey: 'ezekiel', chapter: 30 },
  { bookKey: 'ezekiel', chapter: 31 },
  { bookKey: 'ezekiel', chapter: 32 },
  { bookKey: 'ezekiel', chapter: 33 },
  { bookKey: 'ezekiel', chapter: 34 },
  { bookKey: 'ezekiel', chapter: 35 },
  { bookKey: 'ezekiel', chapter: 36 },
  { bookKey: 'ezekiel', chapter: 37 },
  { bookKey: 'ezekiel', chapter: 38 },
  { bookKey: 'ezekiel', chapter: 39 },
  { bookKey: 'ezekiel', chapter: 40 },
  { bookKey: 'ezekiel', chapter: 41 },
  { bookKey: 'ezekiel', chapter: 42 },
  { bookKey: 'ezekiel', chapter: 43 },
  { bookKey: 'ezekiel', chapter: 44 },
  { bookKey: 'ezekiel', chapter: 45 },
  { bookKey: 'ezekiel', chapter: 46 },
  { bookKey: 'ezekiel', chapter: 47 },
  { bookKey: 'ezekiel', chapter: 48 },
  { bookKey: 'isaiah', chapter: 40 }, // Prophecies of comfort (post-exile focus)
  { bookKey: 'isaiah', chapter: 41 },
  { bookKey: 'isaiah', chapter: 42 },
  { bookKey: 'isaiah', chapter: 43 },
  { bookKey: 'isaiah', chapter: 44 },
  { bookKey: 'isaiah', chapter: 45 },
  { bookKey: 'isaiah', chapter: 46 },
  { bookKey: 'isaiah', chapter: 47 },
  { bookKey: 'isaiah', chapter: 48 },
  { bookKey: 'isaiah', chapter: 49 },
  { bookKey: 'isaiah', chapter: 50 },
  { bookKey: 'isaiah', chapter: 51 },
  { bookKey: 'isaiah', chapter: 52 },
  { bookKey: 'isaiah', chapter: 53 },
  { bookKey: 'isaiah', chapter: 54 },
  { bookKey: 'isaiah', chapter: 55 },
  { bookKey: 'isaiah', chapter: 56 },
  { bookKey: 'isaiah', chapter: 57 },
  { bookKey: 'isaiah', chapter: 58 },
  { bookKey: 'isaiah', chapter: 59 },
  { bookKey: 'isaiah', chapter: 60 },
  { bookKey: 'isaiah', chapter: 61 },
  { bookKey: 'isaiah', chapter: 62 },
  { bookKey: 'isaiah', chapter: 63 },
  { bookKey: 'isaiah', chapter: 64 },
  { bookKey: 'isaiah', chapter: 65 },
  { bookKey: 'isaiah', chapter: 66 },
  { bookKey: 'daniel', chapter: 7 },
  { bookKey: 'daniel', chapter: 8 },
  { bookKey: 'daniel', chapter: 9 },
  { bookKey: 'daniel', chapter: 10 },
  { bookKey: 'daniel', chapter: 11 },
  { bookKey: 'daniel', chapter: 12 },
  { bookKey: 'jeremiah', chapter: 46 },
  { bookKey: 'jeremiah', chapter: 47 },
  { bookKey: 'jeremiah', chapter: 48 },
  { bookKey: 'jeremiah', chapter: 49 },
  { bookKey: 'jeremiah', chapter: 50 },
  { bookKey: 'jeremiah', chapter: 51 },
  { bookKey: 'jeremiah', chapter: 52 },
  // Return to Jerusalem
  { bookKey: 'ezra', chapter: 1 },
  { bookKey: 'ezra', chapter: 2 },
  { bookKey: 'ezra', chapter: 3 },
  { bookKey: 'ezra', chapter: 4 },
  { bookKey: 'haggai', chapter: 1 }, // Prophecy
  { bookKey: 'haggai', chapter: 2 },
  { bookKey: 'zechariah', chapter: 1 }, // Prophecy
  { bookKey: 'zechariah', chapter: 2 },
  { bookKey: 'zechariah', chapter: 3 },
  { bookKey: 'zechariah', chapter: 4 },
  { bookKey: 'zechariah', chapter: 5 },
  { bookKey: 'zechariah', chapter: 6 },
  { bookKey: 'ezra', chapter: 5 },
  { bookKey: 'ezra', chapter: 6 },
  { bookKey: 'zechariah', chapter: 7 },
  { bookKey: 'zechariah', chapter: 8 },
  { bookKey: 'zechariah', chapter: 9 },
  { bookKey: 'zechariah', chapter: 10 },
  { bookKey: 'zechariah', chapter: 11 },
  { bookKey: 'zechariah', chapter: 12 },
  { bookKey: 'zechariah', chapter: 13 },
  { bookKey: 'zechariah', chapter: 14 },
  { bookKey: 'esther', chapter: 1 }, // Events occur between Ezra 6 and 7
  { bookKey: 'esther', chapter: 2 },
  { bookKey: 'esther', chapter: 3 },
  { bookKey: 'esther', chapter: 4 },
  { bookKey: 'esther', chapter: 5 },
  { bookKey: 'esther', chapter: 6 },
  { bookKey: 'esther', chapter: 7 },
  { bookKey: 'esther', chapter: 8 },
  { bookKey: 'esther', chapter: 9 },
  { bookKey: 'esther', chapter: 10 },
  { bookKey: 'ezra', chapter: 7 },
  { bookKey: 'ezra', chapter: 8 },
  { bookKey: 'ezra', chapter: 9 },
  { bookKey: 'ezra', chapter: 10 },
  { bookKey: 'nehemiah', chapter: 1 },
  { bookKey: 'nehemiah', chapter: 2 },
  { bookKey: 'nehemiah', chapter: 3 },
  { bookKey: 'nehemiah', chapter: 4 },
  { bookKey: 'nehemiah', chapter: 5 },
  { bookKey: 'nehemiah', chapter: 6 },
  { bookKey: 'nehemiah', chapter: 7 },
  { bookKey: 'nehemiah', chapter: 8 },
  { bookKey: 'nehemiah', chapter: 9 },
  { bookKey: 'nehemiah', chapter: 10 },
  { bookKey: 'nehemiah', chapter: 11 },
  { bookKey: 'nehemiah', chapter: 12 },
  { bookKey: 'nehemiah', chapter: 13 },
  { bookKey: 'malachi', chapter: 1 }, // Prophecy (final book of the Old Testament)
  { bookKey: 'malachi', chapter: 2 },
  { bookKey: 'malachi', chapter: 3 },
  { bookKey: 'malachi', chapter: 4 },

  // --- PERIOD 6: THE GOSPELS (Life of Christ) ---
  // Birth and Early Life
  { bookKey: 'luke', chapter: 1 },
  { bookKey: 'luke', chapter: 2 },
  { bookKey: 'matthew', chapter: 1 },
  { bookKey: 'matthew', chapter: 2 },
  { bookKey: 'john', chapter: 1 },
  { bookKey: 'mark', chapter: 1 },
  // Ministry
  { bookKey: 'matthew', chapter: 3 },
  { bookKey: 'luke', chapter: 3 },
  { bookKey: 'matthew', chapter: 4 },
  { bookKey: 'luke', chapter: 4 },
  { bookKey: 'john', chapter: 2 },
  { bookKey: 'john', chapter: 3 },
  { bookKey: 'john', chapter: 4 },
  { bookKey: 'matthew', chapter: 5 },
  { bookKey: 'matthew', chapter: 6 },
  { bookKey: 'matthew', chapter: 7 },
  { bookKey: 'luke', chapter: 5 },
  { bookKey: 'mark', chapter: 2 },
  { bookKey: 'mark', chapter: 3 },
  { bookKey: 'matthew', chapter: 8 },
  { bookKey: 'matthew', chapter: 9 },
  { bookKey: 'luke', chapter: 6 },
  { bookKey: 'luke', chapter: 7 },
  { bookKey: 'matthew', chapter: 10 },
  { bookKey: 'matthew', chapter: 11 },
  { bookKey: 'matthew', chapter: 12 },
  { bookKey: 'mark', chapter: 4 },
  { bookKey: 'luke', chapter: 8 },
  { bookKey: 'matthew', chapter: 13 },
  { bookKey: 'mark', chapter: 5 },
  { bookKey: 'mark', chapter: 6 },
  { bookKey: 'john', chapter: 5 },
  { bookKey: 'john', chapter: 6 },
  { bookKey: 'matthew', chapter: 14 },
  { bookKey: 'matthew', chapter: 15 },
  { bookKey: 'mark', chapter: 7 },
  { bookKey: 'mark', chapter: 8 },
  { bookKey: 'matthew', chapter: 16 },
  { bookKey: 'matthew', chapter: 17 },
  { bookKey: 'mark', chapter: 9 },
  { bookKey: 'luke', chapter: 9 },
  { bookKey: 'matthew', chapter: 18 },
  { bookKey: 'john', chapter: 7 },
  { bookKey: 'john', chapter: 8 },
  { bookKey: 'john', chapter: 9 },
  { bookKey: 'john', chapter: 10 },
  { bookKey: 'luke', chapter: 10 },
  { bookKey: 'luke', chapter: 11 },
  { bookKey: 'luke', chapter: 12 },
  { bookKey: 'luke', chapter: 13 },
  { bookKey: 'luke', chapter: 14 },
  { bookKey: 'luke', chapter: 15 },
  { bookKey: 'luke', chapter: 16 },
  { bookKey: 'luke', chapter: 17 },
  { bookKey: 'luke', chapter: 18 },
  { bookKey: 'matthew', chapter: 19 },
  { bookKey: 'mark', chapter: 10 },
  { bookKey: 'john', chapter: 11 },
  { bookKey: 'luke', chapter: 19 },
  { bookKey: 'john', chapter: 12 },
  // Passion Week and Resurrection
  { bookKey: 'matthew', chapter: 20 },
  { bookKey: 'matthew', chapter: 21 },
  { bookKey: 'mark', chapter: 11 },
  { bookKey: 'mark', chapter: 12 },
  { bookKey: 'luke', chapter: 20 },
  { bookKey: 'matthew', chapter: 22 },
  { bookKey: 'matthew', chapter: 23 },
  { bookKey: 'matthew', chapter: 24 },
  { bookKey: 'mark', chapter: 13 },
  { bookKey: 'matthew', chapter: 25 },
  { bookKey: 'luke', chapter: 21 },
  { bookKey: 'luke', chapter: 22 },
  { bookKey: 'matthew', chapter: 26 },
  { bookKey: 'mark', chapter: 14 },
  { bookKey: 'john', chapter: 13 },
  { bookKey: 'john', chapter: 14 },
  { bookKey: 'john', chapter: 15 },
  { bookKey: 'john', chapter: 16 },
  { bookKey: 'john', chapter: 17 },
  { bookKey: 'john', chapter: 18 },
  { bookKey: 'john', chapter: 19 },
  { bookKey: 'matthew', chapter: 27 },
  { bookKey: 'mark', chapter: 15 },
  { bookKey: 'luke', chapter: 23 },
  { bookKey: 'matthew', chapter: 28 },
  { bookKey: 'mark', chapter: 16 },
  { bookKey: 'luke', chapter: 24 },
  { bookKey: 'john', chapter: 20 },
  { bookKey: 'john', chapter: 21 },

  // --- PERIOD 7: ACTS AND THE CHURCH (Acts & Epistles) ---
  // Early Church and Paul's First Journey
  { bookKey: 'acts', chapter: 1 },
  { bookKey: 'acts', chapter: 2 },
  { bookKey: 'acts', chapter: 3 },
  { bookKey: 'acts', chapter: 4 },
  { bookKey: 'acts', chapter: 5 },
  { bookKey: 'acts', chapter: 6 },
  { bookKey: 'acts', chapter: 7 },
  { bookKey: 'acts', chapter: 8 },
  { bookKey: 'acts', chapter: 9 },
  { bookKey: 'acts', chapter: 10 },
  { bookKey: 'acts', chapter: 11 },
  { bookKey: 'acts', chapter: 12 },
  { bookKey: 'james', chapter: 1 }, // Written relatively early
  { bookKey: 'james', chapter: 2 },
  { bookKey: 'james', chapter: 3 },
  { bookKey: 'james', chapter: 4 },
  { bookKey: 'james', chapter: 5 },
  { bookKey: 'acts', chapter: 13 },
  { bookKey: 'acts', chapter: 14 },
  { bookKey: 'galatians', chapter: 1 }, // Written after the first journey
  { bookKey: 'galatians', chapter: 2 },
  { bookKey: 'galatians', chapter: 3 },
  { bookKey: 'galatians', chapter: 4 },
  { bookKey: 'galatians', chapter: 5 },
  { bookKey: 'galatians', chapter: 6 },
  // Paul's Second Journey
  { bookKey: 'acts', chapter: 15 },
  { bookKey: 'acts', chapter: 16 },
  { bookKey: 'acts', chapter: 17 },
  { bookKey: '1-thessalonians', chapter: 1 }, // Written from Corinth
  { bookKey: '1-thessalonians', chapter: 2 },
  { bookKey: '1-thessalonians', chapter: 3 },
  { bookKey: '1-thessalonians', chapter: 4 },
  { bookKey: '1-thessalonians', chapter: 5 },
  { bookKey: '2-thessalonians', chapter: 1 }, // Written shortly after 1 Thess.
  { bookKey: '2-thessalonians', chapter: 2 },
  { bookKey: '2-thessalonians', chapter: 3 },
  { bookKey: 'acts', chapter: 18 },
  // Paul's Third Journey
  { bookKey: 'acts', chapter: 19 },
  { bookKey: '1-corinthians', chapter: 1 }, // Written from Ephesus
  { bookKey: '1-corinthians', chapter: 2 },
  { bookKey: '1-corinthians', chapter: 3 },
  { bookKey: '1-corinthians', chapter: 4 },
  { bookKey: '1-corinthians', chapter: 5 },
  { bookKey: '1-corinthians', chapter: 6 },
  { bookKey: '1-corinthians', chapter: 7 },
  { bookKey: '1-corinthians', chapter: 8 },
  { bookKey: '1-corinthians', chapter: 9 },
  { bookKey: '1-corinthians', chapter: 10 },
  { bookKey: '1-corinthians', chapter: 11 },
  { bookKey: '1-corinthians', chapter: 12 },
  { bookKey: '1-corinthians', chapter: 13 },
  { bookKey: '1-corinthians', chapter: 14 },
  { bookKey: '1-corinthians', chapter: 15 },
  { bookKey: '1-corinthians', chapter: 16 },
  { bookKey: '2-corinthians', chapter: 1 }, // Written from Macedonia
  { bookKey: '2-corinthians', chapter: 2 },
  { bookKey: '2-corinthians', chapter: 3 },
  { bookKey: '2-corinthians', chapter: 4 },
  { bookKey: '2-corinthians', chapter: 5 },
  { bookKey: '2-corinthians', chapter: 6 },
  { bookKey: '2-corinthians', chapter: 7 },
  { bookKey: '2-corinthians', chapter: 8 },
  { bookKey: '2-corinthians', chapter: 9 },
  { bookKey: '2-corinthians', chapter: 10 },
  { bookKey: '2-corinthians', chapter: 11 },
  { bookKey: '2-corinthians', chapter: 12 },
  { bookKey: '2-corinthians', chapter: 13 },
  { bookKey: 'acts', chapter: 20 },
  { bookKey: 'romans', chapter: 1 }, // Written from Corinth
  { bookKey: 'romans', chapter: 2 },
  { bookKey: 'romans', chapter: 3 },
  { bookKey: 'romans', chapter: 4 },
  { bookKey: 'romans', chapter: 5 },
  { bookKey: 'romans', chapter: 6 },
  { bookKey: 'romans', chapter: 7 },
  { bookKey: 'romans', chapter: 8 },
  { bookKey: 'romans', chapter: 9 },
  { bookKey: 'romans', chapter: 10 },
  { bookKey: 'romans', chapter: 11 },
  { bookKey: 'romans', chapter: 12 },
  { bookKey: 'romans', chapter: 13 },
  { bookKey: 'romans', chapter: 14 },
  { bookKey: 'romans', chapter: 15 },
  { bookKey: 'romans', chapter: 16 },
  { bookKey: 'acts', chapter: 21 },
  { bookKey: 'acts', chapter: 22 },
  { bookKey: 'acts', chapter: 23 },
  { bookKey: 'acts', chapter: 24 },
  { bookKey: 'acts', chapter: 25 },
  { bookKey: 'acts', chapter: 26 },
  // Paul's First Roman Imprisonment (Prison Epistles)
  { bookKey: 'acts', chapter: 27 },
  { bookKey: 'acts', chapter: 28 },
  { bookKey: 'ephesians', chapter: 1 },
  { bookKey: 'ephesians', chapter: 2 },
  { bookKey: 'ephesians', chapter: 3 },
  { bookKey: 'ephesians', chapter: 4 },
  { bookKey: 'ephesians', chapter: 5 },
  { bookKey: 'ephesians', chapter: 6 },
  { bookKey: 'colossians', chapter: 1 },
  { bookKey: 'colossians', chapter: 2 },
  { bookKey: 'colossians', chapter: 3 },
  { bookKey: 'colossians', chapter: 4 },
  { bookKey: 'philemon', chapter: 1 },
  { bookKey: 'philippians', chapter: 1 },
  { bookKey: 'philippians', chapter: 2 },
  { bookKey: 'philippians', chapter: 3 },
  { bookKey: 'philippians', chapter: 4 },
  // General Epistles (Late)
  { bookKey: 'hebrews', chapter: 1 },
  { bookKey: 'hebrews', chapter: 2 },
  { bookKey: 'hebrews', chapter: 3 },
  { bookKey: 'hebrews', chapter: 4 },
  { bookKey: 'hebrews', chapter: 5 },
  { bookKey: 'hebrews', chapter: 6 },
  { bookKey: 'hebrews', chapter: 7 },
  { bookKey: 'hebrews', chapter: 8 },
  { bookKey: 'hebrews', chapter: 9 },
  { bookKey: 'hebrews', chapter: 10 },
  { bookKey: 'hebrews', chapter: 11 },
  { bookKey: 'hebrews', chapter: 12 },
  { bookKey: 'hebrews', chapter: 13 },
  { bookKey: '1-peter', chapter: 1 },
  { bookKey: '1-peter', chapter: 2 },
  { bookKey: '1-peter', chapter: 3 },
  { bookKey: '1-peter', chapter: 4 },
  { bookKey: '1-peter', chapter: 5 },
  { bookKey: '2-peter', chapter: 1 },
  { bookKey: '2-peter', chapter: 2 },
  { bookKey: '2-peter', chapter: 3 },
  { bookKey: '1-john', chapter: 1 },
  { bookKey: '1-john', chapter: 2 },
  { bookKey: '1-john', chapter: 3 },
  { bookKey: '1-john', chapter: 4 },
  { bookKey: '1-john', chapter: 5 },
  { bookKey: '2-john', chapter: 1 },
  { bookKey: '3-john', chapter: 1 },
  { bookKey: 'jude', chapter: 1 },
  // Pastoral Epistles (After Paul's release and second imprisonment)
  { bookKey: '1-timothy', chapter: 1 },
  { bookKey: '1-timothy', chapter: 2 },
  { bookKey: '1-timothy', chapter: 3 },
  { bookKey: '1-timothy', chapter: 4 },
  { bookKey: '1-timothy', chapter: 5 },
  { bookKey: '1-timothy', chapter: 6 },
  { bookKey: 'titus', chapter: 1 },
  { bookKey: 'titus', chapter: 2 },
  { bookKey: 'titus', chapter: 3 },
  { bookKey: '2-timothy', chapter: 1 }, // Paul's final letter
  { bookKey: '2-timothy', chapter: 2 },
  { bookKey: '2-timothy', chapter: 3 },
  { bookKey: '2-timothy', chapter: 4 },
  // Revelation
  { bookKey: 'revelation', chapter: 1 },
  { bookKey: 'revelation', chapter: 2 },
  { bookKey: 'revelation', chapter: 3 },
  { bookKey: 'revelation', chapter: 4 },
  { bookKey: 'revelation', chapter: 5 },
  { bookKey: 'revelation', chapter: 6 },
  { bookKey: 'revelation', chapter: 7 },
  { bookKey: 'revelation', chapter: 8 },
  { bookKey: 'revelation', chapter: 9 },
  { bookKey: 'revelation', chapter: 10 },
  { bookKey: 'revelation', chapter: 11 },
  { bookKey: 'revelation', chapter: 12 },
  { bookKey: 'revelation', chapter: 13 },
  { bookKey: 'revelation', chapter: 14 },
  { bookKey: 'revelation', chapter: 15 },
  { bookKey: 'revelation', chapter: 16 },
  { bookKey: 'revelation', chapter: 17 },
  { bookKey: 'revelation', chapter: 18 },
  { bookKey: 'revelation', chapter: 19 },
  { bookKey: 'revelation', chapter: 20 },
  { bookKey: 'revelation', chapter: 21 },
  { bookKey: 'revelation', chapter: 22 },
];
